import React, {memo, useCallback, useEffect, useState} from 'react';

import { dataLayer } from '@weatherbug/common';
import { reduxConnect } from '../../common';
import { UninstallFormView } from './uninstallFormView';
import { validateEmail } from '../../common/utilities/emailValidation';
import { translate } from '../../common/translation/translate';

const { sendFeedback } = dataLayer.actions;

const mapStoreStateToProps = (storeState) => {
    const appState = storeState?.appState?.appState || {};
    const station = appState?.user?.ActiveLocation?.Station;
    const providerId = station?.providerId;
    const stationId = station?.StationId;
    const stationName  = station?.StationName;
    const legal = storeState?.legal || {};
    const termsOfUsageVersion = legal?.legalPolicy?.tou?.version;
    const privacyPolicyVersion = legal?.legalPolicy?.pp?.version;
    const {
        feedbackBaseUrl,
        authId,
        secret,
        stdParameters,
        locality,
        os,
        osVersion,
    } = appState;
    return {
        feedbackBaseUrl,
        authId,
        secret,
        stdParameters,
        locality,
        os,
        osVersion,
        station,
        legal,
        termsOfUsageVersion,
        privacyPolicyVersion,
        providerId,
        stationId,
        stationName,
        cultureInfo: stdParameters?.cultureInfo,
        latitude: locality?.Latitude,
        longitude: locality?.Longitude,
        cityName: locality?.CityName,
        stateProvidence: locality?.TerritoryName,
        country: locality?.CountryIso3Code,
    };
}
const mapDispatchToProps = {
    sendFeedback
};

const verbose = true;
const pushToken = '0000000000000000000000000000000000000000000000000000000011111111';
let defaultEmail = 'no-reply@weatherbug.com';
const pathName = window.location.pathname;

export const UninstallForm = reduxConnect(mapStoreStateToProps, mapDispatchToProps) (memo(({
    supportEmail,
    appInstanceId,
    appVersion,
    appId,
    appName,
    feedbackBaseUrl,
    authId,
    secret,
    stdParameters,
    sendFeedback,
    locality,
    os,
    osVersion,
    station,
    legal,
    cultureInfo,
    termsOfUsageVersion,
    privacyPolicyVersion,
    latitude,
    longitude,
    providerId,
    stationId,
    stationName,
    cityName,
    stateProvidence,
    country
}) => {
    const [internalAppName, setInternalAppName] = useState('');
    const [checkboxStates, setCheckboxStates] = useState({});
    const [otherText, setOtherText] = useState('');
    const [emailAddress, setEmailAddress] = useState();
    const [formValid, setFormValid] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (appName) {
            setInternalAppName(appName);
        } else if (appId) {
            setInternalAppName(appId);
        } else {
            if (pathName.includes('chrome')) {
                setInternalAppName('com.weatherbug.browser.extension.chrome');
            } else {
                setInternalAppName('com.weatherbug.windows.store');
            }
        }
    }, [appId, appName]);

    const handleCheckboxChange = useCallback((event) => {
        const { name, checked } = event.target;
        setCheckboxStates({
            ...checkboxStates,
            [name]: checked
        });
    }, [setCheckboxStates]);

    const handleOtherTextChange = useCallback((event) => {
        setOtherText(event.target.value);
    }, [setOtherText]);

    const handleEmailChange = useCallback((event) => {
        setEmailAddress(event.target.value);
    }, [setEmailAddress]);

    const getCheckedItems = useCallback(() => {
        return Object.keys(checkboxStates).filter(val => checkboxStates[val] === true);
    }, [checkboxStates]);

    const handleFormValidity = () => {
        const checkedItems = getCheckedItems();

        if (checkedItems?.length || otherText) {
            setFormValid(true);
        } else {
            setFormValid(false);
        }
    };

    useEffect(() => {
        handleFormValidity();
    }, [checkboxStates, otherText]);

    const handleFormSubmit = (formSubmitEvent) => {
        formSubmitEvent.preventDefault();

        if (!formValid) {
            alert(translate('selectOption'));
        } else {
            const userEmail = validateEmail(emailAddress) ? emailAddress : defaultEmail;
            const checkedItems = getCheckedItems();
            if (otherText) {
                checkedItems.push(otherText);
            }
            checkedItems.push(supportEmail);
            const comments = checkedItems.join('\n');

            if (appInstanceId && appVersion && stdParameters && appId && internalAppName && feedbackBaseUrl && legal && locality && station && userEmail) {
                const feedbackBody = {
                    User: {
                        RawFeedbackMessage: comments,
                        EmailAddress: userEmail,
                        AppInstanceId: appInstanceId,
                        CultureInfo: cultureInfo,
                        Name: 'anonymous',
                        SignInId: userEmail,
                    },
                    App: {
                        Id: appId,
                        Version: appVersion,
                        Name: internalAppName,
                    },
                    Os: {
                        Type: os,
                        Version: osVersion,
                    },
                    Device: {},
                    Legal: {
                        TermsOfUsageVersion: termsOfUsageVersion,
                        PrivacyPolicyVersion: privacyPolicyVersion
                    },
                    Location: {
                        Selected: {
                            Coordinates: {
                                Latitude: latitude,
                                Longitude: longitude
                            },
                            City: cityName,
                            StateProvidence: stateProvidence,
                            Country: country,
                            Station: {
                                ProviderId: providerId,
                                StationId: stationId,
                                StationName: stationName
                            },
                            LocationName: `${cityName}, ${stateProvidence}`,
                        },
                    },
                    Push: {
                        PushToken: pushToken
                    }
                }

                try {
                    sendFeedback(
                        feedbackBaseUrl,
                        authId,
                        secret,
                        feedbackBody,
                        stdParameters,
                        verbose
                    );

                    setSubmitted(true);
                } catch (error) {
                    console.error(error);
                }
            }
        }
    };

    return (
        <UninstallFormView
            handleCheckboxChange={handleCheckboxChange}
            handleOtherTextChange={handleOtherTextChange}
            handleEmailChange={handleEmailChange}
            handleFormSubmit={handleFormSubmit}
            checkboxStates={checkboxStates}
            submitted={submitted}
        />
    );
}));
